import React, { useState } from "react";
import { Zap, ArrowRight, FileText, Maximize2, Sun, CreditCard, ShieldCheck, TrendingDown, Check } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useCurrency } from "../../lib/currency";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { PRODUCTS, SPEC_ROWS, formatQuote, financePlan, FINANCE, ASSETS, upfrontDiscountRate } from "../../lib/data";
import { ProductLightbox } from "./ProductLightbox";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// Price line that shows a struck-through original when the upfront discount is active.
const PriceLine = ({ usd, cur, upfront, className, testid }) => {
  const { t } = useLang();
  const final = upfront ? usd * (1 - upfrontDiscountRate(cur.id)) : usd;
  return (
    <span className={className} data-testid={testid}>
      {cur.id !== "usd" && <span className="mr-1 text-base font-normal text-white/40">{t.catalog.approx}</span>}
      {upfront && <span className="mr-2 text-base font-normal text-white/35 line-through">{formatQuote(usd, cur)}</span>}
      {formatQuote(final, cur)}
    </span>
  );
};

// Small pill switch used to add the optional solar PV bundle.
const SolarToggle = ({ on, onToggle, addLabel, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testid}
    aria-pressed={on}
    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-300 ${
      on ? "border-lime2 bg-lime2/10 text-lime2" : "border-white/15 text-white/55 hover:border-white/35 hover:text-white/80"
    }`}
  >
    <Sun size={14} className={on ? "text-lime2" : ""} /> {addLabel}
    <span className={`ml-0.5 inline-block h-3.5 w-6 rounded-full transition-colors ${on ? "bg-lime2" : "bg-white/20"} relative`}>
      <span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-black transition-all ${on ? "left-3" : "left-0.5"}`} />
    </span>
  </button>
);

const FeaturedSpotlight = ({ product, cur, upfront, solarOn, onToggleSolar, onZoom }) => {
  const { t } = useLang();
  const s = product.specs;
  const base = product.usd + (solarOn ? product.pvUsd : 0);
  const chips = [
    { l: t.catalog.chipRated, v: s.rated },
    { l: t.catalog.chipPeak, v: s.peak },
    { l: t.catalog.chipStorage, v: s.battery },
    { l: t.catalog.chipEff, v: s.eff },
  ];
  return (
    <Reveal>
      <div
        className="relative overflow-hidden rounded-3xl border border-lime2/25 bg-gradient-to-br from-[#0e1a13] via-charcoal to-black"
        data-testid="featured-spotlight"
      >
        <div className="grid lg:grid-cols-2 gap-0 items-stretch">
          {/* copy */}
          <div className="relative z-10 p-8 md:p-14 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 self-start rounded-full border border-lime2/40 bg-lime2/10 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-lime2">
              <Zap size={12} /> {t.catalog.flagshipTag}
            </span>
            <h3 className="mt-5 font-display font-extrabold tracking-tighter text-3xl md:text-5xl leading-[0.95] text-white">
              {t.catalog.flagshipHeadline}
            </h3>
            <p className="mt-4 text-white/60 max-w-lg">{t.catalog.flagshipSub}</p>

            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {chips.map((c) => (
                <div key={c.l} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/40">{c.l}</p>
                  <p className="mt-1 font-display text-lg font-bold text-lime2">{c.v}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <p className="font-display text-3xl font-extrabold text-white">
                <PriceLine usd={base} cur={cur} upfront={upfront} testid="spotlight-price" />
                <span className="ml-2 text-xs font-normal text-white/40">{t.catalog.from}</span>
              </p>
              <button
                onClick={() => scrollTo("contact")}
                data-testid="spotlight-cta"
                className="inline-flex items-center gap-2 rounded-full bg-lime2 px-6 py-3 text-sm font-semibold text-black hover:brightness-110 transition"
              >
                <Zap size={15} /> {t.catalog.cta} <ArrowRight size={14} />
              </button>
              {product.pdf && (
                <a
                  href={product.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="spotlight-datasheet"
                  className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-lime2 transition-colors"
                >
                  <FileText size={15} /> {t.catalog.datasheet}
                </a>
              )}
            </div>
            <div className="mt-5">
              <SolarToggle on={solarOn} onToggle={onToggleSolar} addLabel={`${t.catalog.solarAdd} (+${formatQuote(product.pvUsd, cur)})`} testid="spotlight-solar-toggle" />
            </div>
          </div>

          {/* image */}
          <button
            onClick={onZoom}
            data-testid="spotlight-image"
            className="group relative min-h-[280px] lg:min-h-full overflow-hidden"
            aria-label={t.catalog.zoomHint}
          >
            <img
              src={product.img}
              alt={`Gesung ${product.model}`}
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent lg:from-charcoal lg:via-charcoal/20" />
            <span className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/50 px-3 py-1.5 text-[11px] text-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Maximize2 size={13} /> {t.catalog.zoomHint}
            </span>
          </button>
        </div>
      </div>
    </Reveal>
  );
};

const SpecCompare = ({ products, cur, upfront, solar, onZoom }) => {
  const { t, lang } = useLang();
  return (
    <Reveal>
      <div className="mt-24">
        <SectionKicker>{t.catalog.specTitle}</SectionKicker>
        <p className="mt-3 text-white/60 max-w-xl">{t.catalog.specSub}</p>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10" data-testid="spec-compare-table">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="bg-white/[0.03]">
                <th className="sticky left-0 z-10 bg-charcoal text-left font-medium text-white/50 px-5 py-4 w-48">
                  {t.catalog.specColSpec}
                </th>
                {products.map((p, i) => (
                  <th key={p.model} className="px-5 py-4 text-left align-bottom">
                    <button
                      onClick={() => onZoom(i)}
                      data-testid={`spec-head-${p.model}`}
                      className="group flex flex-col gap-2 text-left"
                    >
                      <span className="relative h-14 w-24 overflow-hidden rounded-md border border-white/10">
                        <img src={p.img} alt={p.model} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      </span>
                      <span className="font-display text-base font-bold text-white group-hover:text-lime2 transition-colors">
                        Gesung {p.model}
                      </span>
                      {p.flagship && (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-lime2">{t.catalog.flagshipTag}</span>
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SPEC_ROWS.map((row, ri) => (
                <tr key={row.key} className={ri % 2 ? "bg-white/[0.02]" : ""}>
                  <td className="sticky left-0 z-10 bg-charcoal px-5 py-3.5 text-white/50 font-medium border-t border-white/5">
                    {lang === "fr" ? row.fr : row.en}
                  </td>
                  {products.map((p) => (
                    <td
                      key={p.model + row.key}
                      className={`px-5 py-3.5 border-t border-white/5 ${p.flagship ? "text-lime2 font-medium" : "text-white/85"}`}
                    >
                      {row.key === "warranty" ? t.catalog.warrantyYears : p.specs[row.key]}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="sticky left-0 z-10 bg-charcoal px-5 py-4 text-white/50 font-medium border-t border-white/5">
                  {t.catalog.from}
                </td>
                {products.map((p) => (
                  <td key={p.model + "price"} className="px-5 py-4 border-t border-white/5">
                    <PriceLine usd={p.usd + (solar[p.model] ? p.pvUsd : 0)} cur={cur} upfront={upfront} className="font-display text-lg font-extrabold text-lime2" testid={`spec-price-${p.model}`} />
                    {solar[p.model] && (
                      <span className="block text-[10px] text-white/40 mt-0.5">{t.catalog.solarIncl}</span>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Reveal>
  );
};

export const ProductCatalog = () => {
  const { t, lang } = useLang();
  const { cur, curId, setCurId, currencies, upfront, setUpfront, financeMonths, setFinanceMonths } = useCurrency();
  const upfrontPct = Math.round(upfrontDiscountRate(curId) * 100);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [solar, setSolar] = useState({});
  const toggleSolar = (model) => setSolar((s) => ({ ...s, [model]: !s[model] }));
  const flagship = PRODUCTS.find((p) => p.flagship) || PRODUCTS[PRODUCTS.length - 1];
  const flagshipIndex = PRODUCTS.indexOf(flagship);

  return (
    <section id="range" className="relative z-10 py-24 md:py-32 border-t border-white/10 bg-[#161616]" data-testid="catalog-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <SectionKicker>{t.catalog.kicker}</SectionKicker>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl max-w-2xl leading-[0.95]">
            {t.catalog.title}
          </h2>
          <p className="mt-5 text-white/60 max-w-xl">{t.catalog.sub}</p>

          {/* Non-Ghana markets: bold outright-cash discount spotlight */}
          {curId !== "gh" && (
            <div
              data-testid="cash-promo-banner"
              className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-lime2/30 bg-gradient-to-r from-lime2/15 via-emerald2/10 to-transparent p-5"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-lime2 text-charcoal">
                  <TrendingDown size={22} />
                </span>
                <div>
                  <p className="font-display font-extrabold tracking-tight text-xl md:text-2xl text-lime2 leading-none" data-testid="cash-promo-title">
                    {t.catalog.promoTitle.replace("{pct}", upfrontPct)}
                  </p>
                  <p className="mt-1.5 text-xs md:text-sm text-white/60 max-w-md">
                    {t.catalog.promoText.replace("{pct}", upfrontPct)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setUpfront(true)}
                disabled={upfront}
                data-testid="cash-promo-cta"
                className={`sm:ml-auto shrink-0 inline-flex items-center justify-center gap-2 rounded-full h-11 px-6 font-semibold transition-colors ${
                  upfront ? "bg-lime2/15 text-lime2 cursor-default" : "bg-lime2 text-charcoal hover:bg-white"
                }`}
              >
                {upfront ? <><Check size={16} /> {t.catalog.promoApplied}</> : t.catalog.promoApply}
              </button>
            </div>
          )}

          {/* Currency selector — quote prices in local rates (auto-detected from timezone) */}
          <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="currency-selector">
            <span className="mr-1 text-[11px] uppercase tracking-[0.18em] text-white/40">{t.catalog.priceIn}</span>
            {currencies.map((c) => (
              <button
                key={c.id}
                onClick={() => setCurId(c.id)}
                data-testid={`currency-${c.id}`}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-300 ${
                  c.id === curId
                    ? "border-lime2 bg-lime2/10 text-lime2"
                    : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                }`}
              >
                {c.id === "usd" ? "USD $" : `${lang === "fr" ? c.labelFR : c.labelEN} ${c.symbol}`}
              </button>
            ))}
          </div>

          {/* Payment options — upfront discount + Ghana credit facility */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setUpfront(!upfront)}
              data-testid="upfront-toggle"
              aria-pressed={upfront}
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-300 ${
                upfront ? "border-lime2 bg-lime2/10 text-lime2" : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {t.catalog.upfrontLabel.replace("{pct}", upfrontPct)}
              <span className={`ml-0.5 inline-block h-3.5 w-6 rounded-full transition-colors ${upfront ? "bg-lime2" : "bg-white/20"} relative`}>
                <span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-black transition-all ${upfront ? "left-3" : "left-0.5"}`} />
              </span>
            </button>
            {curId === "gh" && (
              <span
                data-testid="gh-credit-badge"
                className="inline-flex items-center gap-2 rounded-full border border-emerald2/40 bg-emerald2/10 px-3.5 py-1.5 text-xs text-lime2"
              >
                <CreditCard size={13} /> {t.catalog.ghCredit}
              </span>
            )}
          </div>

          {/* Ghana instalment term selector */}
          {curId === "gh" && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="finance-term-selector">
              <span className="mr-1 text-[11px] uppercase tracking-[0.18em] text-white/40">{t.catalog.financeLabel} · {t.catalog.financeTerm}</span>
              {FINANCE.terms.map((m) => (
                <button
                  key={m}
                  onClick={() => setFinanceMonths(m)}
                  data-testid={`finance-term-${m}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-300 ${
                    m === financeMonths ? "border-lime2 bg-lime2/10 text-lime2" : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {m} {t.catalog.monthsShort}
                </button>
              ))}
            </div>
          )}

          {/* Warranty trust strip */}
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="warranty-strip">
            <span className="inline-flex items-center gap-2 text-sm text-white/70">
              <ShieldCheck size={16} className="text-lime2" /> {t.catalog.warrantyGen}
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-white/70">
              <Sun size={16} className="text-lime2" /> {t.catalog.warrantyPanel}
            </span>
          </div>
        </Reveal>

        {/* Featured flagship spotlight */}
        <div className="mt-12">
          <FeaturedSpotlight
            product={flagship}
            cur={cur}
            upfront={upfront}
            solarOn={!!solar[flagship.model]}
            onToggleSolar={() => toggleSolar(flagship.model)}
            onZoom={() => setLightboxIndex(flagshipIndex)}
          />
        </div>

        {/* Product grid */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRODUCTS.map((p, i) => {
            const solarOn = !!solar[p.model];
            const base = p.usd + (solarOn ? p.pvUsd : 0);
            return (
            <Reveal key={p.model} delay={(i % 4) * 0.08}>
              <div className="group h-full flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-charcoal hover:border-lime2/40 transition-colors duration-500" data-testid={`catalog-${p.model}`}>
                <button
                  onClick={() => setLightboxIndex(i)}
                  data-testid={`catalog-image-${p.model}`}
                  className="relative aspect-square overflow-hidden bg-black block w-full"
                  aria-label={t.catalog.zoomHint}
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(0,168,107,0.3),transparent_60%)]" />
                  <img src={p.img || ASSETS.product} alt={`Gesung ${p.model}`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-4 left-4 text-[11px] font-mono text-lime2 border border-lime2/40 rounded px-2 py-0.5 bg-black/40 backdrop-blur-sm">{p.kw} kW</span>
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-semibold text-black bg-lime2 rounded-full px-2 py-1 shadow-sm" data-testid={`catalog-warranty-${p.model}`}>
                    <ShieldCheck size={11} /> {t.catalog.warrantyBadge}
                  </span>
                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/50 px-2.5 py-1 text-[10px] text-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Maximize2 size={11} /> {t.catalog.zoomHint}
                  </span>
                </button>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-2xl font-bold">Gesung {p.model}</h3>
                  <p className="text-white/55 text-sm mt-2 flex-1">{p.use}</p>
                  <div className="mt-5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t.catalog.from}</p>
                    <PriceLine usd={base} cur={cur} upfront={upfront} className="font-display text-3xl font-extrabold text-lime2 mt-1 block" testid={`catalog-price-${p.model}`} />
                    <p className="text-[11px] text-white/40 mt-1">
                      {[solarOn ? t.catalog.solarIncl : null, upfront ? t.catalog.upfrontNote.replace("{pct}", upfrontPct) : null].filter(Boolean).join(" · ") || t.catalog.excl}
                    </p>
                    {curId === "gh" && (() => {
                      const plan = financePlan(base, financeMonths);
                      return (
                        <>
                        <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-lime2/25 bg-lime2/5 px-2 py-1 text-[11px] text-lime2" data-testid={`catalog-finance-${p.model}`}>
                          <CreditCard size={11} /> {formatQuote(plan.monthly, cur)}{t.catalog.perMonth} × {plan.months} · {t.catalog.financeDown} · +{Math.round(plan.rate * 100)}% {t.catalog.interestSuffix}
                        </p>
                        <p className="mt-1 text-[11px] text-white/40" data-testid={`catalog-finance-total-${p.model}`}>
                          {t.catalog.totalPayable}: {formatQuote(plan.total, cur)}
                        </p>
                        </>
                      );
                    })()}
                  </div>
                  <div className="mt-4">
                    <SolarToggle
                      on={solarOn}
                      onToggle={() => toggleSolar(p.model)}
                      addLabel={`${t.catalog.solarAdd} (+${formatQuote(p.pvUsd, cur)})`}
                      testid={`catalog-solar-${p.model}`}
                    />
                  </div>
                  <button
                    onClick={() => scrollTo("contact")}
                    data-testid={`catalog-cta-${p.model}`}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 group-hover:border-lime2 group-hover:text-lime2 text-white/80 font-medium py-3 text-sm transition-colors duration-300"
                  >
                    <Zap size={15} /> {t.catalog.cta} <ArrowRight size={14} />
                  </button>
                  {p.pdf && (
                    <a
                      href={p.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`catalog-datasheet-${p.model}`}
                      className="mt-3 inline-flex items-center justify-center gap-2 text-[13px] text-white/50 hover:text-lime2 transition-colors duration-300"
                    >
                      <FileText size={14} /> {t.catalog.datasheet}
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          );})}
        </div>

        {/* Official pricing terms (from the Gesung final price list) */}
        <p className="mt-6 max-w-3xl text-xs text-white/40 leading-relaxed" data-testid="catalog-price-terms">
          {t.catalog.priceTerms}
        </p>

        {/* Spec comparison table */}
        <SpecCompare products={PRODUCTS} cur={cur} upfront={upfront} solar={solar} onZoom={(i) => setLightboxIndex(i)} />
      </div>

      <ProductLightbox
        products={PRODUCTS}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(i) => setLightboxIndex(i)}
      />
    </section>
  );
};
